import React, { ReactElement, useCallback, useEffect, type JSX } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { FontSizes, FontWeights, Text } from '@calm-web/design-system';

import Branding from '@/components/SimplifiedSignup/Components/Branding';
import CampaignCodeEntry from '@/components/SimplifiedSignup/Components/CampaignCodeEntry';
import ContentBasedImage from '@/components/SimplifiedSignup/Components/ContentBasedImage';
import { campaignPartnershipEventProps } from '@/components/SimplifiedSignup/configs/Campaigns/analytics';
import { useSimplifiedSignupContext } from '@/components/SimplifiedSignup/Context';
import { useAuthPrompt } from '@/context/auth/AuthPromptProvider';
import { useSimplifiedSignupModalContext } from '@/context/SimplifiedSignupModalContextProvider';
import { useAnalytics, useExperimentClient } from '@/hooks/analytics';
import { useFormattedCurrency } from '@/hooks/purchase';
import {
	useCampaignConfigState,
	usePartnerState,
	usePricesState,
	usePurchaseParamsState,
	useUserState,
} from '@/hooks/store';
import { useIsMobile } from '@/hooks/ui/useIsMobile';
import { useComponentDidMount } from '@/hooks/utils/useComponentDidMount';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import { setCampaignConfig } from '@/store/campaignConfig/actions';
import getUrl from '@/utils/getUrl';

import SisuAuthForm from '../Components/SisuAuthForm';
import { TwoColumnWrapper } from '../styles';
import messages from './messages';
import {
	AccountPageWrap,
	AccountValuePropsList,
	AuthColumnWrapper,
	Collapsible,
	FAQContentWrapper,
	FAQWrapper,
	HorizontalLine,
	ValuePropsListWrapper,
} from './styles';
import { AccountScreenConfig } from './types';

const Account = ({ config }: { config?: AccountScreenConfig }) => {
	const { formatMessage } = useIntl();
	const user = useUserState();
	const partner = usePartnerState();
	const campaignConfig = useCampaignConfigState();
	const { name, activeScreenConfig, coBranded, logo, logPageViewEvent } = useSimplifiedSignupContext();
	const {
		backgroundKey,
		upsellHeader,
		upsellSubHeader,
		contentBasedImage,
		brandingPosition = 'right',
		hasPremiumSubCallback,
		FAQs,
		showHeader,
		upsellBullets = [],
		valuePropsTitleSize,
		valuePropsTitleColor,
		isValuePropsListOrdered,
		valuePropsColor,
		valuePropsButtonColor,
		AdditionalContent,
		isWithinModal,
		onAuthSuccess,
		hideValueProps,
		ignoreSubscriptionStatus,
	} = activeScreenConfig as AccountScreenConfig;
	const { setIsModalOpen } = useSimplifiedSignupModalContext();
	const routerPush = useRouterPush();
	const { logEvent } = useAnalytics();
	const [isMobile] = useIsMobile();
	const prices = usePricesState();
	const purchaseParams = usePurchaseParamsState();
	const experimentClient = useExperimentClient();
	const { authPrompt } = useAuthPrompt();
	const formatCurrency = useFormattedCurrency();
	const dispatch = useDispatch();
	// Gates SisuAuthForm behind CampaignCodeEntry until it reports validated (via
	// onValidated), including the codeless case. Driven by the store, not local state, so
	// Context's routing effect (which decides whether an already-logged-in user can reach
	// Payment) and this render gate agree on the same signal -- offer_code alone can't be
	// that signal, since [slug].tsx seeds it straight from the URL, unvalidated. Undefined
	// (falsy) on every fresh SSR load, so CampaignCodeEntry still mounts once even when
	// requires_code is already false: its own effect needs to run once to report
	// onValidated(undefined) and clear a stray offer_code for a campaign that takes no code.
	const codeValidated = campaignConfig?.codeValidated ?? false;

	// CampaignCodeEntry holds this in a useCallback dep and an effect dep -- a new reference
	// every render would rebuild that callback and re-run that effect on every Account render.
	const handleCodeValidated = useCallback(
		(code: string | undefined): void => {
			// Always writes, even when code is undefined (the codeless case): a stray
			// ?offer_code= from a tracking param or stale link must not survive into the
			// purchase call for a campaign CampaignCodeEntry decided needs no code at all.
			if (campaignConfig) {
				dispatch(setCampaignConfig({ ...campaignConfig, offer_code: code, codeValidated: true }));
			}
		},
		[campaignConfig, dispatch],
	);

	useComponentDidMount(() => {
		logPageViewEvent('account');
		logEvent({
			eventName: 'Login Form : Landed',
			eventProps: {
				source: name,
				// Fires at landing, before CampaignCodeEntry has validated anything, so product can
				// measure drop-off at the code page. promo_code is therefore the link's code or
				// "null" here. The redeemed code travels on the purchase Success event.
				...(campaignConfig ? campaignPartnershipEventProps(campaignConfig) : {}),
			},
		});
	});

	useEffect(() => {
		// Partners (and now campaigns) get their own ineligibility message from SisuAuthForm on
		// this same screen — this effect's job is only the OTHER flows that have no such message
		// and need the hard redirect instead.
		if (
			user?.subscription?.valid &&
			!partner &&
			!campaignConfig &&
			hasPremiumSubCallback &&
			!ignoreSubscriptionStatus
		) {
			hasPremiumSubCallback(routerPush, setIsModalOpen);
		}
	}, [
		user,
		routerPush,
		hasPremiumSubCallback,
		partner,
		campaignConfig,
		setIsModalOpen,
		ignoreSubscriptionStatus,
	]);

	function getTrigger(faq: {
		trigger: MessageDescriptor;
		children: MessageDescriptor;
		placeholders?: { [key: string]: string | number | boolean | (() => JSX.Element) };
	}): {
		trigger: ReactElement;
		children: ReactElement;
	} {
		return {
			trigger: (
				<Text el="h3" size={FontSizes.base} weight={FontWeights.Demi}>
					{formatMessage(faq.trigger, faq.placeholders)}
				</Text>
			),
			children: (
				<Text weight={FontWeights.Regular}>
					{formatMessage(faq.children, {
						...faq.placeholders,
						br: (
							<>
								<br />
								<br />
							</>
						),
						site: children => (
							<a target="_blank" href={`${getUrl('www')}/subscribe`} rel="noreferrer">
								{children}
							</a>
						),
						ul: children => <ul>{children}</ul>,
						li: children => <li>{children}</li>,
					})}
				</Text>
			),
		};
	}

	const getBrandingHeader = () => {
		if (showHeader) {
			return null;
		}

		return <Branding backgroundColor={activeScreenConfig?.backgroundKey} coBranded={coBranded} logo={logo} />;
	};

	const getValueListTitle = () => {
		if (!upsellHeader) return null;

		const { message, placeholders = {} } = upsellHeader({
			authFormMode: authPrompt,
			prices,
			purchaseParams,
			formatCurrency,
		});

		return formatMessage(message, { ...placeholders, br: <br /> });
	};

	const getValueListSubTitle = () => {
		if (!upsellSubHeader) return null;

		const { message, placeholders = {} } = upsellSubHeader();

		return formatMessage(message, { ...placeholders, br: <br /> });
	};

	useEffect(() => {
		if (user && onAuthSuccess) {
			onAuthSuccess(experimentClient);
		}
	}, [user, experimentClient, onAuthSuccess]);

	return (
		<AccountPageWrap
			backgroundKey={backgroundKey}
			isWithinModal={isWithinModal}
			isCompact={isWithinModal || hideValueProps}
		>
			<TwoColumnWrapper isWithinModal={isWithinModal}>
				<AuthColumnWrapper>
					{((brandingPosition === 'right' && !isMobile) || (isMobile && !isWithinModal)) &&
						getBrandingHeader()}
					{campaignConfig && !codeValidated ? (
						<CampaignCodeEntry
							campaign={campaignConfig}
							requiresCode={campaignConfig.requires_code}
							initialCode={campaignConfig.offer_code}
							contactEmail={campaignConfig.contact_email}
							onValidated={handleCodeValidated}
						/>
					) : (
						<SisuAuthForm />
					)}
				</AuthColumnWrapper>
				{!hideValueProps && (
					<ValuePropsListWrapper showHeader={showHeader} isWithinModal={isWithinModal}>
						{((brandingPosition === 'left' && !isMobile) || (isMobile && isWithinModal)) &&
							getBrandingHeader()}
						{contentBasedImage && <ContentBasedImage {...contentBasedImage} />}
						<AccountValuePropsList
							isOrdered={isValuePropsListOrdered}
							titleSize={valuePropsTitleSize}
							titleColor={valuePropsTitleColor}
							propSize={FontSizes.sm}
							copy={!(isMobile && isWithinModal) ? upsellBullets : undefined}
							title={getValueListTitle()}
							subTitle={getValueListSubTitle()}
							buttonColor={valuePropsButtonColor ?? 'buttonPurple'}
							textColor={valuePropsColor ?? 'black'}
							fontWeight={FontWeights.Medium}
							gapSize="20px"
						/>
					</ValuePropsListWrapper>
				)}
			</TwoColumnWrapper>
			{AdditionalContent && <AdditionalContent />}
			{FAQs && FAQs.length && (
				<FAQWrapper isCompact={hideValueProps}>
					<FAQContentWrapper>
						<div>
							<Text
								el="h2"
								align="center"
								size={FontSizes['2xl']}
								color="buttonBlue"
								weight={FontWeights.Demi}
							>
								{formatMessage(messages.faqHeading)}
							</Text>
						</div>
						<div>
							<HorizontalLine />
							{FAQs.map(faq => (
								<div key={faq.id}>
									<Collapsible {...getTrigger(faq)} />
									<HorizontalLine />
								</div>
							))}
						</div>
					</FAQContentWrapper>
				</FAQWrapper>
			)}
		</AccountPageWrap>
	);
};

export default Account;
